import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Alert,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import {
  LocationOn,
  DirectionsBus,
  Warning,
  Schedule,
  People,
  BugReport,
  Download,
} from '@mui/icons-material';
import { collection, doc, endAt, getDoc, getDocs, onSnapshot, orderBy, query, startAt } from 'firebase/firestore';
import { format } from 'date-fns';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { Operation, StationLog, StopRecord } from '../types/operation';
import { Route } from '../types/route';
import { useStationPassengers } from '../hooks/useStationPassengers';
import { formatAverageMinutes, koreanDate, summarizeArrivalTiming, summarizeStations } from '../utils/stationPassengers';
import { exportToCSV } from '../utils/csvExporter';

interface StationOperationRecord {
  id: string;
  operationId: string;
  operationDate: string;
  routeId: string;
  routeName: string;
  vehicleNumber: string;
  driverName?: string;
  stationId: string;
  stationName: string;
  sequence: number;
  scheduledTime?: string;
  actualArrivalTime?: Date;
  departureTime?: Date;
  delayMinutes?: number;
  passengers: number;
  status: 'arrived' | 'skipped' | 'pending';
  source: 'stationLogs' | 'stopRecords';
}

const getLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getTimestampDate = (value: unknown): Date | undefined => {
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  return undefined;
};

export const StationOperationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminType } = useAuth();
  const [selectedDate, setSelectedDate] = useState<string>(searchParams.get('date') || koreanDate(new Date()));
  const [operations, setOperations] = useState<Operation[]>([]);
  const [routeNames, setRouteNames] = useState<Record<string, string>>({});
  const [selectedStationKey, setSelectedStationKey] = useState<string>('all');
  const [showTestData, setShowTestData] = useState(searchParams.get('test') === '1');
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const routeFilter = useMemo(() => {
    switch (adminType) {
      case 'route1':
        return '강서구청';
      case 'route2':
        return '하단';
      default:
        return null;
    }
  }, [adminType]);

  const passengers = useStationPassengers(selectedDate, showTestData, routeFilter);
  const routes = passengers.routes;
  const stationOrders = passengers.stations;

  useEffect(() => {
    if (!selectedDate) return;

    setLoading(true);
    setOperations([]);
    let active = true;

    const q = query(
      collection(db, 'operations'),
      orderBy('__name__'),
      startAt(selectedDate),
      endAt(`${selectedDate}\uf8ff`)
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const data = snapshot.docs
          .map((operationDoc) => {
            const docData = operationDoc.data();
            const operationDate = operationDoc.id.split('_')[0];

            return {
              id: operationDoc.id,
              operationDate,
              routeId: docData.routeId || '',
              routeName: docData.routeName || '',
              vehicleNumber: docData.vehicleNumber || '',
              driverName: docData.driverName || '',
              status: docData.status || '',
              startTime: docData.startTime,
              endTime: docData.endTime,
              currentStopIndex: docData.currentStopIndex || 0,
              totalStops: docData.totalStops || 0,
              isDelayed: docData.isDelayed || false,
              delayMinutes: docData.delayMinutes || 0,
              totalPassengers: docData.totalPassengers || 0,
              isHot: docData.isHot || 0,
              isCold: docData.isCold || 0,
              isDanger: docData.isDanger || 0,
              isTestMode: docData.isTestMode || false,
              stationLogs: docData.stationLogs || [],
              stopRecords: docData.stopRecords || [],
            } as Operation;
          })
          .filter((operation) => (showTestData ? operation.isTestMode === true : !operation.isTestMode));

        const routeNameMap: Record<string, string> = {};
        const missingRouteIds = new Set<string>();

        data.forEach((operation) => {
          if (operation.routeName) {
            routeNameMap[operation.routeId] = operation.routeName;
          } else if (operation.routeId) {
            missingRouteIds.add(operation.routeId);
          }
        });

        for (const routeId of Array.from(missingRouteIds)) {
          try {
            const routeDoc = await getDoc(doc(db, showTestData ? 'test_routes' : 'routes', routeId));
            routeNameMap[routeId] = routeDoc.exists() ? (routeDoc.data() as Route).name : routeId;
          } catch (error) {
            console.error(`Error fetching route ${routeId}:`, error);
            routeNameMap[routeId] = routeId;
          }
        }

        const filteredData = routeFilter
          ? data.filter((operation) => {
              const routeName = operation.routeName || routeNameMap[operation.routeId] || operation.routeId;
              return routeName.includes(routeFilter);
            })
          : data;

        if (!active) return;
        setOperations(filteredData);
        setRouteNames(routeNameMap);
        setLoading(false);
      },
      (error) => {
        console.error('Error subscribing to station operations:', error);
        setLoading(false);
      }
    );

    return () => { active = false; unsubscribe(); };
  }, [selectedDate, routeFilter, showTestData]);

  const records = useMemo<StationOperationRecord[]>(() => {
    const stationRecords = operations.flatMap((operation) => {
      const routeName = routeNames[operation.routeId] || operation.routeName || operation.routeId;

      if (operation.stationLogs && operation.stationLogs.length > 0) {
        return operation.stationLogs.map((log: StationLog, index: number) => {
          const actualArrivalTime = getTimestampDate(log.actualArrivalTime);

          return {
            id: `${operation.id}_${log.stationId || index}`,
            operationId: operation.id,
            operationDate: operation.operationDate,
            routeId: operation.routeId,
            routeName,
            vehicleNumber: operation.vehicleNumber,
            driverName: operation.driverName,
            stationId: log.stationId || `${log.stationName}_${index}`,
            stationName: log.stationName || '정류장명 없음',
            sequence: index + 1,
            scheduledTime: log.scheduledTime,
            actualArrivalTime,
            delayMinutes: log.delayMinutes,
            passengers: 0,
            status: log.status === 'skipped' ? 'skipped' : actualArrivalTime ? 'arrived' : 'pending',
            source: 'stationLogs',
          } as StationOperationRecord;
        });
      }

      return (operation.stopRecords || []).map((stop: StopRecord, index: number) => {
        const arrivalTime = getTimestampDate(stop.arrivalTime);

        return {
          id: `${operation.id}_${stop.stopId || index}`,
          operationId: operation.id,
          operationDate: operation.operationDate,
          routeId: operation.routeId,
          routeName,
          vehicleNumber: operation.vehicleNumber,
          driverName: operation.driverName,
          stationId: stop.stopId || `${stop.stopName}_${index}`,
          stationName: stop.stopName || '정류장명 없음',
          sequence: index + 1,
          actualArrivalTime: arrivalTime,
          departureTime: getTimestampDate(stop.departureTime),
          passengers: 0,
          status: stop.isSkipped ? 'skipped' : arrivalTime ? 'arrived' : 'pending',
          source: 'stopRecords',
        } as StationOperationRecord;
      });
    });

    return stationRecords.sort((a, b) => {
      const stationCompare = a.stationName.localeCompare(b.stationName, 'ko');
      if (stationCompare !== 0) return stationCompare;

      const timeCompare = (a.scheduledTime || '').localeCompare(b.scheduledTime || '');
      if (timeCompare !== 0) return timeCompare;

      return a.routeName.localeCompare(b.routeName, 'ko');
    });
  }, [operations, routeNames]);

  const stationOrderMap = useMemo(() => {
    const orderMap = new Map<string, number>();

    stationOrders.forEach((station, index) => {
      const orderValue = station.order === Number.MAX_SAFE_INTEGER ? Number.MAX_SAFE_INTEGER - index : station.order;
      orderMap.set(station.id, orderValue);
      orderMap.set(station.name, orderValue);
    });

    return orderMap;
  }, [stationOrders]);

  const getStationOrder = useCallback((stationId: string, stationName: string): number => {
    return stationOrderMap.get(stationId) ?? stationOrderMap.get(stationName) ?? Number.MAX_SAFE_INTEGER;
  }, [stationOrderMap]);

  const stationOptions = useMemo(() => {
    const optionMap = new Map<string, { value: string; label: string }>();
    const selectedDay = getLocalDate(selectedDate).getDay();
    const firebaseWeekday = selectedDay === 0 ? 7 : selectedDay;

    routes.forEach((route) => {
      const routeName = route.name || route.id;
      if (routeFilter && !routeName.includes(routeFilter)) return;
      if (route.operatingDays && !route.operatingDays.includes(firebaseWeekday)) return;

      (route.stops || []).forEach((stop) => {
        if (!stop.stationName) return;

        const value = `${stop.stationName}__${stop.stationName}`;
        if (!optionMap.has(value)) {
          optionMap.set(value, { value, label: stop.stationName });
        }
      });
    });

    [...records, ...passengers.entries.filter(entry => entry.date === selectedDate)].forEach((record) => {
      const value = `${record.stationName}__${record.stationName}`;
      if (!optionMap.has(value)) {
        optionMap.set(value, { value, label: record.stationName });
      }
    });

    return Array.from(optionMap.values()).sort((a, b) => {
      const orderCompare = getStationOrder(a.label, a.label) - getStationOrder(b.label, b.label);
      if (orderCompare !== 0) return orderCompare;

      return a.label.localeCompare(b.label, 'ko');
    });
  }, [getStationOrder, records, routeFilter, routes, selectedDate, passengers.entries]);

  useEffect(() => {
    if (selectedStationKey !== 'all' && !stationOptions.some((option) => option.value === selectedStationKey)) {
      setSelectedStationKey('all');
    }
  }, [selectedStationKey, stationOptions]);

  const stationSummaries = useMemo(() =>
    summarizeStations(selectedDate, routes, operations, passengers.entries).sort((a, b) => {
      const order = getStationOrder(a.stationId, a.stationName) - getStationOrder(b.stationId, b.stationName);
      return order || a.stationName.localeCompare(b.stationName, 'ko');
    }), [getStationOrder, selectedDate, routes, operations, passengers.entries]);

  const filteredStationSummaries = useMemo(() => {
    if (selectedStationKey === 'all') return stationSummaries;

    return stationSummaries.filter(
      (summary) => `${summary.stationName}__${summary.stationName}` === selectedStationKey
    );
  }, [selectedStationKey, stationSummaries]);

  const handleDownloadCSV = async (fullMonth = false) => {
    if (!selectedDate || passengers.loading || passengers.error) return;
    const selected = getLocalDate(selectedDate);
    const monthStart = format(new Date(selected.getFullYear(), selected.getMonth(), 1), 'yyyy-MM-dd');
    const endDate = fullMonth
      ? format(new Date(selected.getFullYear(), selected.getMonth() + 1, 0), 'yyyy-MM-dd') : selectedDate;
    setIsExporting(true);
    try {
      const snapshot = await getDocs(query(collection(db, 'operations'), orderBy('__name__'),
        startAt(monthStart), endAt(`${endDate}\uf8ff`)));
      const names = new Map(routes.map(route => [route.id, route.name]));
      const monthlyOperations = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id,
        operationDate: doc.id.split('_')[0] } as Operation)).filter(operation =>
        (showTestData ? operation.isTestMode === true : !operation.isTestMode) &&
        (!routeFilter || (names.get(operation.routeId) || operation.routeName || '').includes(routeFilter)));
      const rows = [];
      // Index once so exporting a month doesn't repeatedly scan all checkins/operations.
      const entriesByDate = new Map<string, typeof passengers.entries>();
      passengers.entries.forEach(entry => {
        if (!entriesByDate.has(entry.date)) entriesByDate.set(entry.date, []);
        entriesByDate.get(entry.date)!.push(entry);
      });
      const operationsByDate = new Map<string, Operation[]>();
      monthlyOperations.forEach(operation => {
        if (!operationsByDate.has(operation.operationDate)) operationsByDate.set(operation.operationDate, []);
        operationsByDate.get(operation.operationDate)!.push(operation);
      });
      for (let day = 1; day <= Number(endDate.slice(-2)); day++) {
        const date = `${monthStart.slice(0, 8)}${String(day).padStart(2, '0')}`;
        rows.push(...summarizeStations(date, routes, operationsByDate.get(date) || [], entriesByDate.get(date) || [])
          .filter(summary => selectedStationKey === 'all' || `${summary.stationName}__${summary.stationName}` === selectedStationKey)
          .map(summary => ({ ...summary, operationDate: date })));
      }
      rows.sort((a, b) => getStationOrder(a.stationId, a.stationName) - getStationOrder(b.stationId, b.stationName) ||
        a.stationName.localeCompare(b.stationName, 'ko') || a.operationDate.localeCompare(b.operationDate));
      if (!rows.length) { alert('다운로드할 데이터가 없습니다.'); return; }
      exportToCSV(rows, [
        { key: 'stationName', header: '정류장명' }, { key: 'operationDate', header: '운행 일자' },
        { key: 'routes', header: '노선 수' }, { key: 'records', header: '기록 수' },
        { key: 'passedRecords', header: '통과 수' }, { key: 'delayedRecords', header: '지연 수' },
        { key: 'averageDelayMinutes', header: '평균 지연(분)', formatter: value => value === null ? '' : value.toFixed(1) },
        { key: 'earlyArrivalRecords', header: '조기 도착 수' },
        { key: 'averageEarlyArrivalMinutes', header: '평균 조기 도착(분)', formatter: value => value === null ? '' : value.toFixed(1) },
        { key: 'passengers', header: '이용 인원(승하차 합계)' },
        { key: 'reservationBoarding', header: '예약 승차' }, { key: 'reservationAlighting', header: '예약 하차' },
        { key: 'qrBoarding', header: 'QR 승차' }, { key: 'qrAlighting', header: 'QR 하차' },
      ], `정류장별_운행기록_${monthStart}_${endDate}.csv`);
    } catch (error) {
      console.error('Error exporting station operations:', error);
      alert('CSV 다운로드 중 오류가 발생했습니다.');
    } finally { setIsExporting(false); }
  };

  const statistics = useMemo(() => {
    const timing = summarizeArrivalTiming(records);
    const totalPassengers = passengers.totals.get(selectedDate) || 0;
    const selectedDay = getLocalDate(selectedDate).getDay();
    const firebaseWeekday = selectedDay === 0 ? 7 : selectedDay;
    const scheduledRouteIds = new Set(
      routes
        .filter((route) => {
          const routeName = route.name || route.id;
          if (routeFilter && !routeName.includes(routeFilter)) return false;
          return !route.operatingDays || route.operatingDays.includes(firebaseWeekday);
        })
        .map((route) => route.id)
    );

    return {
      stationCount: stationOptions.length,
      recordCount: records.length,
      routeCount: scheduledRouteIds.size,
      ...timing,
      totalPassengers,
    };
  }, [records, routeFilter, routes, selectedDate, stationOptions, passengers.totals]);

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn sx={{ fontSize: 32, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                정류장별 운행기록
              </Typography>
              <Typography variant="body2" color="text.secondary">
                선택한 날짜의 노선 운행 데이터를 정류장 단위로 확인합니다.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {adminType === 'super' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showTestData}
                    onChange={(event) => setShowTestData(event.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BugReport sx={{ fontSize: 18, color: showTestData ? '#f59e0b' : '#94a3b8' }} />
                    <Typography variant="body2" color={showTestData ? '#f59e0b' : 'text.secondary'}>
                      테스트 데이터
                    </Typography>
                  </Box>
                }
              />
            )}

            <TextField
              type="date"
              label="조회 날짜"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
          </Box>
        </Box>

        {passengers.error && <Alert severity="error" sx={{ mb: 2 }}>{passengers.error}</Alert>}
        {!passengers.loading && !passengers.error && (passengers.unmatchedTotal > 0 || passengers.invalidTimestamps > 0) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            월 전체 정류장 미매칭 승하차 {passengers.unmatchedTotal}건 (조회일 {passengers.unmatched.get(selectedDate) || 0}건) · 날짜 오류 {passengers.invalidTimestamps}건. 일부 정류장 집계가 누락될 수 있습니다.
          </Alert>
        )}
        {loading || passengers.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <LocationOn sx={{ fontSize: 32, color: '#667eea' }} />
                  <Typography variant="h6" fontWeight={600}>
                    정류장 수
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {statistics.stationCount.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {statistics.routeCount}개 노선
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <DirectionsBus sx={{ fontSize: 32, color: '#10b981' }} />
                  <Typography variant="h6" fontWeight={600}>
                    정류장 기록
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#10b981' }}>
                  {statistics.recordCount.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  노선별 정류장 방문 기록
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Warning sx={{ fontSize: 32, color: '#f59e0b' }} />
                  <Typography variant="h6" fontWeight={600}>
                    지연 기록
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#f59e0b' }}>
                  {statistics.delayedRecords.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  평균 {formatAverageMinutes(statistics.averageDelayMinutes)}
                  {statistics.recordCount > 0 && ` · ${((statistics.delayedRecords / statistics.recordCount) * 100).toFixed(1)}% 지연`}
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Schedule sx={{ fontSize: 32, color: '#0284c7' }} />
                  <Typography variant="h6" fontWeight={600}>조기 도착</Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#0284c7' }}>
                  {statistics.earlyArrivalRecords.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  평균 {formatAverageMinutes(statistics.averageEarlyArrivalMinutes)}
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <People sx={{ fontSize: 32, color: '#3b82f6' }} />
                  <Typography variant="h6" fontWeight={600}>
                    이용 인원
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#3b82f6' }}>
                  {passengers.error ? '-' : statistics.totalPassengers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  체크인 1건 = 1명 (승하차 중복 제외)
                </Typography>
              </Paper>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  정류장별 요약
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel>정류장 필터</InputLabel>
                    <Select
                      value={selectedStationKey}
                      label="정류장 필터"
                      onChange={(event) => setSelectedStationKey(event.target.value)}
                    >
                      <MenuItem value="all">전체 정류장</MenuItem>
                      {stationOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={() => handleDownloadCSV(false)}
                    disabled={isExporting || passengers.loading || !!passengers.error}
                  >
                    {isExporting ? 'CSV 생성 중...' : '월초~조회일 CSV 다운로드'}
                  </Button>
                  <Button variant="outlined" startIcon={<Download />} onClick={() => handleDownloadCSV(true)}
                    disabled={isExporting || passengers.loading || !!passengers.error}>
                    월간 CSV 다운로드
                  </Button>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                이용 인원은 예약·QR 승하차 합계입니다. 상단은 체크인 건수이므로 정류장 합계와 다릅니다.
                인원은 월 데이터 최초 조회 기준이며, 월간 CSV는 선택한 월 전체의 일별 자료입니다.
                지연·조기 도착 평균은 각각 해당 도착 기록만 대상으로 계산하며, 정시·미도착·통과 기록은 제외합니다.
              </Typography>
              {filteredStationSummaries.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography color="text.secondary">선택한 날짜의 정류장별 운행 기록이 없습니다.</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>정류장명</TableCell>
                        <TableCell align="center">노선 수</TableCell>
                        <TableCell align="center">기록 수</TableCell>
                        <TableCell align="center">통과</TableCell>
                        <TableCell align="center">지연</TableCell>
                        <TableCell align="center">조기 도착</TableCell>
                        <TableCell align="center">이용 인원</TableCell>
                        {['예약 승차', '예약 하차', 'QR 승차', 'QR 하차'].map(label => <TableCell key={label} align="center">{label}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredStationSummaries.map((summary) => (
                        <TableRow
                          key={`${summary.stationId}_${summary.stationName}`}
                          hover
                          onClick={() => {
                            navigate({
                              pathname: `/operation/stations/${encodeURIComponent(summary.stationId)}`,
                              search: createSearchParams({
                                date: selectedDate,
                                name: summary.stationName,
                                test: showTestData ? '1' : '0',
                              }).toString(),
                            });
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationOn sx={{ fontSize: 18, color: '#667eea' }} />
                              <Typography variant="body2" fontWeight={600}>
                                {summary.stationName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{summary.routes.toLocaleString()}</TableCell>
                          <TableCell align="center">{summary.records.toLocaleString()}</TableCell>
                          <TableCell align="center">
                            {summary.passedRecords > 0 ? (
                              <Chip label={`${summary.passedRecords}건`} color="default" size="small" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          {([
                            { count: summary.delayedRecords, average: summary.averageDelayMinutes, color: 'warning' },
                            { count: summary.earlyArrivalRecords, average: summary.averageEarlyArrivalMinutes, color: 'info' },
                          ] as const).map(({ count, average, color }) => (
                            <TableCell key={color} align="center">
                              {count > 0 ? <>
                                <Chip label={`${count}건`} color={color} size="small" />
                                <Typography variant="caption" display="block">평균 {formatAverageMinutes(average)}</Typography>
                              </> : <Typography variant="body2" color="text.secondary">-</Typography>}
                            </TableCell>
                          ))}
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <People sx={{ fontSize: 16, color: '#667eea' }} />
                              <Typography variant="body2" fontWeight={600}>
                                {passengers.error ? '-' : `${summary.passengers.toLocaleString()}명`}
                              </Typography>
                            </Box>
                          </TableCell>
                          {(['reservationBoarding', 'reservationAlighting', 'qrBoarding', 'qrAlighting'] as const).map(field => (
                            <TableCell key={field} align="center">{passengers.error ? '-' : summary[field].toLocaleString()}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

          </>
        )}
      </Box>
    </MainLayout>
  );
};
